import { CSSProperties, FC, useMemo } from 'react';
import RESOURCES from '~/resources';
import { Pagination, Navigation, Autoplay } from 'swiper';
import { IKImage } from 'imagekitio-react';
import useStringTemplate from '~/hooks/useStringTemplate';

import emptyCar from '~/assets/images/EmptyCar.png';
import {
  carouselContainer,
  imageKitStyle,
  imageStyle,
  StyledSwiper,
  StyledSwiperSlide,
} from './useStyles';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import Image from 'next/image';

interface Props {
  images: (string | null)[];
  containerHeight?: number | string;
  containerWidth?: number | string;
  ikThumb: string;
  customStyle?: CSSProperties;
  carImageAlt?: string;
  autoSlide?: boolean;
  isNavigationDots?: boolean;
  customContainerStyle?: CSSProperties;
  carId?: number;
  isLazyLoad?: boolean;
}
export interface IIkStyle {
  width: string;
  height: string;
}

const CarImagesKitCarousel: FC<Props> = ({
  images = [],
  containerHeight = 220,
  containerWidth = 326,
  ikThumb,
  customStyle,
  carImageAlt,
  autoSlide = false,
  isNavigationDots = true,
  customContainerStyle,
  carId = 0,
  isLazyLoad = true,
}) => {
  const formatString = useStringTemplate();
  const noImages = useMemo(() => images.length === 0, [images]);

  const mappedCardsImages = useMemo(() => {
    const carouselImages = !noImages
      ? images.map((image, index) => {
          if (!image) return;

          const carImageAltText = formatString(carImageAlt, {
            imageIndex: index + 1,
            totalImages: images.length,
          });

          return (
            <StyledSwiperSlide
              key={image}
              style={{
                height: containerHeight,
                width: containerWidth,
              }}
              role="region"
              tabIndex={0}
              aria-label={RESOURCES.IMG_GALLERY}
            >
              {image.includes('https') ? (
                <IKImage
                  src={image}
                  transformation={[{ n: ikThumb }]}
                  style={{ ...imageKitStyle, ...customStyle }}
                  alt={carImageAltText}
                  loading={isLazyLoad ? 'lazy' : 'eager'}
                  className={`carousel-image-${carId}`}
                />
              ) : (
                <IKImage
                  path={image}
                  style={{ ...imageKitStyle, ...customStyle }}
                  transformation={[{ n: ikThumb }]}
                  alt={carImageAltText}
                  loading={isLazyLoad ? 'lazy' : 'eager'}
                  className={`carousel-image-${carId}`}
                />
              )}
            </StyledSwiperSlide>
          );
        })
      : [
          <StyledSwiperSlide
            style={{ height: containerHeight }}
            role="region"
            tabIndex={0}
            key={emptyCar.src}
          >
            <Image
              width={200}
              height={300}
              src={emptyCar.src}
              style={imageStyle}
              alt={carImageAlt ?? ''}
              fetchPriority="low"
              className={`carousel-image-${carId}`}
            />
          </StyledSwiperSlide>,
        ];

    return carouselImages;
  }, [
    isLazyLoad,
    containerHeight,
    containerWidth,
    customStyle,
    ikThumb,
    images,
    carImageAlt,
    formatString,
    noImages,
    carId,
  ]);

  const swiperModules = useMemo(() => {
    const modules = [Pagination, Navigation];
    if (autoSlide) {
      modules.push(Autoplay);
    }
    return modules;
  }, [autoSlide]);

  return (
    <>
      <StyledSwiper
        noImages={noImages}
        isNavigationDots={isNavigationDots}
        grabCursor
        style={{ ...carouselContainer, ...customContainerStyle }}
        navigation
        cssMode
        pagination
        modules={swiperModules}
        autoplay={{
          delay: 3500,
          disableOnInteraction: false,
        }}
      >
        {mappedCardsImages}
      </StyledSwiper>
    </>
  );
};

export default CarImagesKitCarousel;
